import React, { useCallback } from 'react';
import Button from '@atlaskit/button';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItem,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import type { IssueType } from '@atlassian/jira-admin-pages-issue-types-common/src/types.tsx';
import { CellWrapper } from '@atlassian/jira-admin-pages-issue-types-common/src/ui/index.tsx';
import {
	getEditIssueTypeUrl,
	getDeleteIssueTypeUrl,
	getTranslateIssueTypeUrl,
} from '@atlassian/jira-admin-pages-issue-types-common/src/utils.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { messages } from './messages';

export type Props = {
	issueTypeId: string;
	issueType: IssueType;
	canDelete: boolean;
	canTranslate: boolean;
	hierarchyLevel: number;
};

export const ActionsCell = ({
	canDelete,
	canTranslate,
	issueTypeId,
	issueType,
	hierarchyLevel,
}: Props) => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAnalyticsOnDropdownOpened = useCallback(
		({ isOpen }: { isOpen: boolean }) => {
			if (isOpen) {
				const analyticsEvent = createAnalyticsEvent({});

				fireUIAnalytics(analyticsEvent, 'dropdownMenu clicked', 'screenActions', {
					issueTypeId: issueType.id,
					issueTypeType: issueType.type,
					issueTypeHasDescription: issueType.description !== '',
					issueTypeHasAvatar: issueType.iconUrl !== '',
				});
			}
		},
		[createAnalyticsEvent, issueType.description, issueType.iconUrl, issueType.id, issueType.type],
	);

	const fireAnalyticsOnEditClicked = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({});

		fireUIAnalytics(analyticsEvent, 'dropdownMenu clicked', 'edit', {
			issueTypeId: issueType.id,
			issueTypeType: issueType.type,
			issueTypeHasDescription: issueType.description !== '',
			issueTypeHasAvatar: issueType.iconUrl !== '',
		});
	}, [
		createAnalyticsEvent,
		issueType.description,
		issueType.iconUrl,
		issueType.id,
		issueType.type,
	]);

	const fireAnalyticsOnDeleteClicked = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({});

		fireUIAnalytics(analyticsEvent, 'dropdownMenu clicked', 'delete', {
			issueTypeId: issueType.id,
			issueTypeType: issueType.type,
			issueTypeHasDescription: issueType.description !== '',
			issueTypeHasAvatar: issueType.iconUrl !== '',
		});
	}, [
		createAnalyticsEvent,
		issueType.description,
		issueType.iconUrl,
		issueType.id,
		issueType.type,
	]);

	const fireAnalyticsOnTranslateClicked = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({});

		fireUIAnalytics(analyticsEvent, 'dropdownMenu clicked', 'translate', {
			issueTypeId: issueType.id,
			issueTypeType: issueType.type,
			issueTypeHasDescription: issueType.description !== '',
			issueTypeHasAvatar: issueType.iconUrl !== '',
		});
	}, [
		createAnalyticsEvent,
		issueType.description,
		issueType.iconUrl,
		issueType.id,
		issueType.type,
	]);

	return (
		<CellWrapper last>
			<DropdownMenu
				placement="bottom-end"
				trigger={({ triggerRef, ...restProps }: CustomTriggerProps) => (
					<Button
						{...restProps}
						iconBefore={<MoreIcon label={formatMessage(messages.actions)} />}
						ref={triggerRef}
						appearance="subtle"
					/>
				)}
				onOpenChange={fireAnalyticsOnDropdownOpened}
			>
				<DropdownItemGroup>
					<DropdownItem
						href={getEditIssueTypeUrl(issueTypeId)}
						onClick={fireAnalyticsOnEditClicked}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(messages.edit)}
					</DropdownItem>
					{canDelete && (
						<DropdownItem
							href={getDeleteIssueTypeUrl(issueTypeId)}
							onClick={fireAnalyticsOnDeleteClicked}
							UNSAFE_shouldDisableRouterLink
						>
							{formatMessage(messages.delete)}
						</DropdownItem>
					)}
					{canTranslate && (
						<DropdownItem
							href={getTranslateIssueTypeUrl(hierarchyLevel)}
							onClick={fireAnalyticsOnTranslateClicked}
							UNSAFE_shouldDisableRouterLink
						>
							{formatMessage(messages.translate)}
						</DropdownItem>
					)}
				</DropdownItemGroup>
			</DropdownMenu>
		</CellWrapper>
	);
};
